import { Trans, useLingui } from '@lingui/react/macro'
import { Toggle } from 'designSystem/components/Toggle'
import {
  PaneEmptyState,
  RevisionComparePane,
  type RevisionComparePaneHandle,
} from 'features/knowledgeDocumentViewer/components/revisionCompare/RevisionComparePane'
import { RevisionDetailChangeList } from 'features/knowledgeDocumentViewer/components/revisionCompare/RevisionDetailChangeList'
import type { RevisionDetailEventRow } from 'features/knowledgeDocumentViewer/components/revisionCompare/revisionDiffModel'
import type { CanonicalRevisionModel } from 'features/knowledgeDocumentViewer/components/revisionCompare/revisionMemberCanonical'
import { RevisionPairPicker } from 'features/knowledgeDocumentViewer/components/revisionCompare/RevisionPairPicker'
import { RevisionStatusLegend } from 'features/knowledgeDocumentViewer/components/revisionCompare/RevisionStatusLegend'
import {
  type RevisionSideDisplayMeta,
  useRevisionCompareSession,
} from 'features/knowledgeDocumentViewer/components/revisionCompare/useRevisionCompareSession'
import type { FimRevisionDiffDetailEntry } from 'features/knowledgeDocumentViewer/hooks/useRevisionDiffDetails'
import { Eye, EyeOff } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

const Layout = styled.div`
  display: flex;
  min-height: 0;
  flex: 1;
  flex-direction: column;
  gap: 1rem;
  container-type: inline-size;
`

const HeaderControlsRow = styled.div`
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`

const DetailsPanel = styled.div`
  display: flex;
  flex: 2 1 0;
  min-height: 8rem;
  flex-direction: column;
  gap: 1rem;
  overflow-y: auto;
`

const DetailsStatus = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  color: var(--muted-foreground);
  font-size: 0.8125rem;
`

const NarrowSideToggleGroup = styled.div<{ $expanded: boolean }>`
  display: none;
  gap: 0.25rem;

  @container (width <= 40rem) {
    display: ${(props) => (props.$expanded ? 'none' : 'flex')};
  }
`

const PanesRow = styled.div<{
  $expanded: boolean
  $narrowSide: NarrowSide
}>`
  display: grid;
  min-height: 0;
  flex: 3 1 0;
  grid-template-columns: ${(props) => (props.$expanded ? '1fr' : '1fr 1fr')};
  gap: 0.75rem;

  @container (width <= 40rem) {
    flex: 1 1 0;
    grid-template-columns: 1fr;
    grid-auto-rows: minmax(0, 1fr);

    ${(props) =>
      props.$expanded
        ? ''
        : `> [data-side]:not([data-side='${props.$narrowSide}']) {
      display: none;
    }`}
  }
`

const PaneSlot = styled.div<{ $hidden: boolean }>`
  display: ${(props) => (props.$hidden ? 'none' : 'flex')};
  min-width: 0;
  min-height: 0;
  flex-direction: column;

  > * {
    flex: 1;
  }
`

type ExpandedSide = 'from' | 'to' | null
type NarrowSide = 'from' | 'to'

type RevisionCompareContentProps = {
  open: boolean
  details: readonly FimRevisionDiffDetailEntry[]
  canonicalRevisionModel: CanonicalRevisionModel
  currentBqDocumentId: string
  currentPageNum: number
  initialDetailId?: string
}

export function RevisionCompareContent({
  open,
  details,
  canonicalRevisionModel,
  currentBqDocumentId,
  currentPageNum,
  initialDetailId,
}: RevisionCompareContentProps) {
  const { t } = useLingui()
  const unknownRevisionLabel = t`unknown`

  const {
    hasSelectedDetail,
    selectedDetailId,
    showOverlay,
    setShowOverlay,
    fromImageUrl,
    isLoadingFromImage,
    toImageUrl,
    isLoadingToImage,
    fromMeta,
    toMeta,
    detailViewModel,
    isLoadingDetailFile,
    selectedEventId,
    selectedObjectIds,
    regionFrom,
    regionTo,
    selectObject,
    selectEvent,
    hiddenStatuses,
    toggleStatus,
    hiddenImpacts,
    toggleImpact,
    selectPair,
    fromDocumentId,
    toDocumentId,
    familyMembers,
  } = useRevisionCompareSession({
    open,
    details,
    canonicalRevisionModel,
    currentBqDocumentId,
    currentPageNum,
    initialDetailId,
  })

  const fromPaneRef = useRef<RevisionComparePaneHandle>(null)
  const toPaneRef = useRef<RevisionComparePaneHandle>(null)
  const [expandedSide, setExpandedSide] = useState<ExpandedSide>(null)
  const [narrowSide, setNarrowSide] = useState<NarrowSide>('to')

  // Reset after the DOM resize so the fit is computed against the new viewport.
  useEffect(() => {
    if (expandedSide === null) return
    const paneRef = expandedSide === 'from' ? fromPaneRef : toPaneRef
    const frame = requestAnimationFrame(() => paneRef.current?.resetView())
    return () => cancelAnimationFrame(frame)
  }, [expandedSide])

  const handleShapeClick = (objectId: string) => {
    selectObject(objectId)
    const boxes = detailViewModel?.boxesByObjectId.get(objectId)
    const fromBox = boxes?.fromBox ?? null
    const toBox = boxes?.toBox ?? null
    fromPaneRef.current?.zoomToBox(fromBox ?? toBox)
    toPaneRef.current?.zoomToBox(toBox ?? fromBox)
  }

  const handleSelectEvent = (event: RevisionDetailEventRow) => {
    selectEvent(event)
    fromPaneRef.current?.zoomToBox(event.fromBox ?? event.toBox)
    toPaneRef.current?.zoomToBox(event.toBox ?? event.fromBox)
  }

  const fromSide = detailViewModel?.from
  const toSide = detailViewModel?.to

  const paneTitle = (meta: RevisionSideDisplayMeta) => {
    const revisionLabel = meta.revision || unknownRevisionLabel
    return t`Rev ${revisionLabel}`
  }
  const paneSubtitle = (meta: RevisionSideDisplayMeta) =>
    [meta.documentTitle, meta.revisionDate].filter(Boolean).join(' · ')

  return hasSelectedDetail ? (
    <Layout>
      <RevisionPairPicker
        members={familyMembers}
        fromDocumentId={fromDocumentId}
        toDocumentId={toDocumentId}
        details={details}
        canonicalEndpoint={canonicalRevisionModel.canonicalEndpoint}
        onSelectPair={selectPair}
      />
      <HeaderControlsRow>
        <Toggle
          variant='outline'
          size='sm'
          pressed={showOverlay}
          onPressedChange={setShowOverlay}
          aria-label={t`Toggle change highlighting`}>
          {showOverlay ? (
            <Eye size={14} strokeWidth={1.5} />
          ) : (
            <EyeOff size={14} strokeWidth={1.5} />
          )}
          <Trans>Highlight changes</Trans>
        </Toggle>
        <NarrowSideToggleGroup $expanded={expandedSide !== null}>
          <Toggle
            variant='outline'
            size='sm'
            pressed={narrowSide === 'from'}
            onPressedChange={() => setNarrowSide('from')}>
            <Trans>Before</Trans>
          </Toggle>
          <Toggle
            variant='outline'
            size='sm'
            pressed={narrowSide === 'to'}
            onPressedChange={() => setNarrowSide('to')}>
            <Trans>After</Trans>
          </Toggle>
        </NarrowSideToggleGroup>
      </HeaderControlsRow>

      {detailViewModel ? (
        <RevisionStatusLegend
          countsByStatus={detailViewModel.countsByStatus}
          hiddenStatuses={hiddenStatuses}
          onToggle={toggleStatus}
          countsByImpact={detailViewModel.countsByImpact}
          hiddenImpacts={hiddenImpacts}
          onToggleImpact={toggleImpact}
        />
      ) : null}

      <PanesRow $expanded={expandedSide !== null} $narrowSide={narrowSide}>
        <PaneSlot data-side='from' $hidden={expandedSide === 'to'}>
          <RevisionComparePane
            key={`from-${selectedDetailId}`}
            ref={fromPaneRef}
            title={paneTitle(fromMeta)}
            subtitle={paneSubtitle(fromMeta)}
            imageUrl={fromImageUrl}
            isLoadingImage={isLoadingFromImage}
            shapes={fromSide?.shapes ?? []}
            selectedObjectIds={selectedObjectIds}
            selectedRegion={regionFrom}
            showOverlay={showOverlay}
            onShapeClick={handleShapeClick}
            isExpanded={expandedSide === 'from'}
            onToggleExpand={() =>
              setExpandedSide((s) => (s === 'from' ? null : 'from'))
            }
          />
        </PaneSlot>
        <PaneSlot data-side='to' $hidden={expandedSide === 'from'}>
          <RevisionComparePane
            key={`to-${selectedDetailId}`}
            ref={toPaneRef}
            title={paneTitle(toMeta)}
            subtitle={paneSubtitle(toMeta)}
            imageUrl={toImageUrl}
            isLoadingImage={isLoadingToImage}
            shapes={toSide?.shapes ?? []}
            selectedObjectIds={selectedObjectIds}
            selectedRegion={regionTo}
            showOverlay={showOverlay}
            onShapeClick={handleShapeClick}
            isExpanded={expandedSide === 'to'}
            onToggleExpand={() =>
              setExpandedSide((s) => (s === 'to' ? null : 'to'))
            }
          />
        </PaneSlot>
      </PanesRow>

      {expandedSide === null ? (
        <DetailsPanel>
          {detailViewModel ? (
            <RevisionDetailChangeList
              viewModel={detailViewModel}
              selectedEventId={selectedEventId}
              onSelectEvent={handleSelectEvent}
              fromImageUrl={fromImageUrl}
              toImageUrl={toImageUrl}
            />
          ) : (
            <DetailsStatus>
              {isLoadingDetailFile ? (
                <Trans>Loading changes…</Trans>
              ) : (
                <Trans>Change details are unavailable.</Trans>
              )}
            </DetailsStatus>
          )}
        </DetailsPanel>
      ) : null}
    </Layout>
  ) : (
    <PaneEmptyState>
      <Trans>No revision diff available.</Trans>
    </PaneEmptyState>
  )
}
